<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="500px"
  >
    <!--<editor-fold desc="Dialog Header">-->
    <template slot="dialogTitle">
      <span v-if="yachtEquipmentPackCategoryDirty.id">
        Edit Equipment pack category
      </span>
      <span v-else>
        Add New Equipment pack category
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Form">-->
    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-text-field
        v-model="yachtEquipmentPackCategoryDirty.name"
        v-validate="'required'"
        name="name"
        label="Name"
        placeholder="e.g. Optional equipment"
        outlined
        :error-messages="veeErrors.collect('name')"
      />

      <v-text-field
        v-model="yachtEquipmentPackCategoryDirty.name_fr"
        name="name_fr"
        label="Name (FR)"
        placeholder="e.g. Équipement optionnel"
        outlined
      />

      <v-select
        v-model="yachtEquipmentPackCategoryDirty.color"
        :items="colors"
        outlined
        item-text="name"
        item-value="color"
        label="Color"
      >
        <template v-slot:item="{ item: color }">
          <v-avatar
            size="14px"
            :color="color.color"
            class="mr-2"
          />
          {{ color.name }}
        </template>
      </v-select>

      <v-row no-gutters>
        <v-col cols="4">
          <v-text-field
            v-model="yachtEquipmentPackCategoryDirty.order"
            name="order"
            label="Order"
            outlined
            type="numeric"
          />
        </v-col>
      </v-row>

      <v-switch
        v-model="yachtEquipmentPackCategoryDirty.required"
        label="Equipment from this category is required on yacht"
      />

      <v-switch
        v-model="yachtEquipmentPackCategoryDirty.is_cdr"
        label="Equipment from this category counts into CDR costs"
      />

      <v-switch
        v-model="yachtEquipmentPackCategoryDirty.is_navigare_option"
        label="Grouped as custom option"
      />
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="yachtEquipmentPackCategoryDirty.id && $can('delete', 'YachtEquipmentPackCategory')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Equipment pack category?"
      @confirmed="deleteYachtEquipmentPackCategory"
    />
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapState } from 'vuex'
import YachtEquipmentPackCategory from '@/models/YachtEquipmentPackCategory'
import ContentDialog from '@/components/helpers/Dialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import colors from 'vuetify/es5/util/colors'

export default {
    name: 'YachtEquipmentPackCategoryEditDialog',
    components: {
      ContentDialog,
      ConfirmDeleteDialog
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      yachtEquipmentPackCategory: {
        type: Object,
        default: () => YachtEquipmentPackCategory.emptyModel
      }
    },
    data() {
      return {
        yachtEquipmentPackCategoryDirty: cloneDeep(this.yachtEquipmentPackCategory),
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      colors() {
        return Object.keys(colors).filter(c => c !== 'shades').map((key, index) => ({
          name: key.replace(/([A-Z])/g, ' $1').replace(/^./, function(str) { return str.toUpperCase() }),
          color: colors[key].base
        }))
      },
      ...mapState('settings', ['yachtManufacturers', 'countries'])
    },
    watch: {
      yachtEquipmentPackCategory() {
        this.yachtEquipmentPackCategoryDirty = cloneDeep(this.yachtEquipmentPackCategory)
      }
    },
    methods: {
      async submit() {
        let validation = await this.$validator.validate()

        if (!validation) return

        this.save()
      },
      async save() {
        this.submitting = true

        const data = {
          id: this.yachtEquipmentPackCategoryDirty.id,
          name: this.yachtEquipmentPackCategoryDirty.name,
          name_fr: this.yachtEquipmentPackCategoryDirty.name_fr,
          color: this.yachtEquipmentPackCategoryDirty.color,
          required: this.yachtEquipmentPackCategoryDirty.required,
          is_cdr: this.yachtEquipmentPackCategoryDirty.is_cdr,
          is_navigare_option: this.yachtEquipmentPackCategoryDirty.is_navigare_option,
          order: this.yachtEquipmentPackCategoryDirty.order
        }

        try {
          let yachtEquipmentPackCategory = await YachtEquipmentPackCategory.api.save(data, this.yachtEquipmentPackCategoryDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Equipment pack category saved',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.yachtEquipmentPackCategoryDirty.id ? 'yachtEquipmentPackCategory:updated' : 'yachtEquipmentPackCategory:created', yachtEquipmentPackCategory)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        } finally {
          this.submitting = false
        }
      },
      async deleteYachtEquipmentPackCategory() {
        this.deleting = true

        try {
          await YachtEquipmentPackCategory.api.destroy(this.yachtEquipmentPackCategoryDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Equipment pack category deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('yachtEquipmentPackCategory:deleted', this.yachtEquipmentPackCategoryDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      }
    }
  }
</script>
